// "All records" means all records (ADR 0007, PAGINATION_API_CONTRACT.md).
// Page until a short page, with the page size taken from the FIRST response,
// never from what was asked for and never from X-Total-Count.
import { API_CAPABILITIES, DEFAULT_REQUEST_SIZE, type ApiCapabilities } from './capabilities'
import { resource } from './resource'
import type { ListQuery, ListResult, RecordId } from './types'

export interface AllPagesParams {
  sort?: string
  order?: NonNullable<ListQuery['order']>
  filter?: NonNullable<ListQuery['filter']>
  params?: NonNullable<ListQuery['params']>
  /** Defaults to the published cap, else 500 (PARITY Q5). */
  requestSize?: number
  signal?: AbortSignal
  /** For tests; defaults to API_CAPABILITIES. */
  capabilities?: ApiCapabilities
}

export interface AllPagesResult<T> {
  rows: T[]
  /** X-Total-Count of the first response; undefined when unknown. Diagnostics only. */
  total: number | undefined
  /** How many requests the fetch took. */
  requests: number
}

function throwIfAborted(signal: AbortSignal | undefined) {
  if (signal?.aborted) {
    throw signal.reason instanceof Error ? signal.reason : new DOMException('Aborted', 'AbortError')
  }
}

/** A row's identity: its id, else its content (rows without an id). */
function rowKey(row: unknown): unknown {
  const id = (row as { id?: RecordId | null } | null)?.id
  return id === undefined || id === null ? JSON.stringify(row) : id
}

function dedupe<T>(rows: T[]): T[] {
  const seen = new Set<RecordId>()
  const out: T[] = []
  for (const row of rows) {
    const id = (row as { id?: RecordId | null } | null)?.id
    if (id === undefined || id === null) {
      out.push(row)
      continue
    }
    if (seen.has(id)) continue
    seen.add(id)
    out.push(row)
  }
  return out
}

/** Whether the server says there is another page; undefined when it does not say. */
function nextSignal(page: ListResult<unknown>, capabilities: ApiCapabilities): boolean | undefined {
  return capabilities.supportsNextPageIndicator ? page.hasNextPage : undefined
}

export async function getAllPages<T>(
  res: string,
  {
    sort,
    order,
    filter,
    params,
    signal,
    capabilities = API_CAPABILITIES,
    requestSize = capabilities.maxPageSize ?? DEFAULT_REQUEST_SIZE
  }: AllPagesParams = {}
): Promise<AllPagesResult<T>> {
  const client = resource<T>(res)
  const base = {
    ...(sort !== undefined && { sort }),
    ...(order !== undefined && { order }),
    ...(filter !== undefined && { filter }),
    ...(params !== undefined && { params })
  }
  const opts = { capabilities, ...(signal && { signal }) }

  throwIfAborted(signal)
  const first = await client.list({ ...base, start: 0, end: requestSize }, opts)
  let requests = 1
  const pageSize = first.rows.length
  if (pageSize === 0) return { rows: [], total: first.total, requests }

  let rows = first.rows
  let last: ListResult<T> = first
  const seen = new Set(first.rows.map(rowKey))
  // A short first page is unambiguous only when the cap is published and we asked within it.
  const firstIsFinal =
    nextSignal(first, capabilities) === false ||
    (capabilities.maxPageSize !== undefined &&
      requestSize <= capabilities.maxPageSize &&
      pageSize < requestSize)

  if (!firstIsFinal) {
    let offset = pageSize
    for (;;) {
      const more = nextSignal(last, capabilities)
      if (more === false) break
      throwIfAborted(signal)
      last = await client.list({ ...base, start: offset, end: offset + pageSize }, opts)
      requests += 1
      // An endpoint that ignores _start/_end (a small lookup) answers every
      // page with the same full page: nothing new means there is no more.
      const fresh = last.rows.filter((row) => !seen.has(rowKey(row)))
      if (last.rows.length > 0 && fresh.length === 0) break
      for (const row of fresh) seen.add(rowKey(row))
      rows = rows.concat(last.rows)
      if (nextSignal(last, capabilities) === undefined && last.rows.length < pageSize) break
      if (last.rows.length === 0) break
      offset += last.rows.length
    }
  }

  return { rows: dedupe(rows), total: first.total, requests }
}
